import type { ElectricityPriceScenario, ElplanPartition } from 'api/elplan/elplan.types'
import {
  buildPartitionAliases,
  buildSubtypeAliases,
  hasDynamicAliasKeyCollision,
  type SubtypeAliasOption,
} from 'helpers/optimize.helper/optimize.helper'
import type { TFunction } from 'i18next'
import type { FeatureRequirements } from 'ui/components/AuthContext/AuthContext'
import {
  type DayAheadTabDefinition,
  DayAheadTabId,
  type RenderableScenarioPartitionResolution,
  type ScenarioPartitionResolution,
} from './DayAheadTradingView.types'

const ELPLAN_PARTITION_MODE_ALIAS = 'elplan_partition_mode'
const PARTITION_ONLY_MODE = 'partition_only'

export function resolveSelectedScenarioPartition({
  scenario,
  partitions,
  selectedPartitionKey,
}: {
  scenario: ElectricityPriceScenario
  partitions: ElplanPartition[]
  selectedPartitionKey: string
}): ScenarioPartitionResolution {
  const partitionMode = scenario.uiconfig_override_alias[ELPLAN_PARTITION_MODE_ALIAS]
  const hasPartitionModeAlias = ELPLAN_PARTITION_MODE_ALIAS in scenario.uiconfig_override_alias
  const hasPartitionBindings = scenario.partition_bindings.length > 0

  if (hasPartitionBindings && hasPartitionModeAlias) {
    return { mode: 'error', reason: 'partition-mode-conflict' }
  }

  if (!hasPartitionBindings && !hasPartitionModeAlias) {
    return { mode: 'unpartitioned' }
  }

  if (!hasPartitionBindings && partitionMode !== PARTITION_ONLY_MODE) {
    return { mode: 'error', reason: 'invalid-partition-mode' }
  }

  if (hasDynamicAliasKeyCollision(partitions.map(({ key }) => key))) {
    return { mode: 'error', reason: 'alias-key-collision' }
  }

  const partition = partitions.find(({ key }) => key === selectedPartitionKey)
  if (!partition) {
    return { mode: 'error', reason: 'partition-not-found' }
  }

  if (partitionMode === PARTITION_ONLY_MODE) {
    return { mode: 'partition-only', partition }
  }

  const bindings = scenario.partition_bindings.filter(({ partition_id }) => partition_id === partition.id)
  if (bindings.length === 0) {
    return { mode: 'error', reason: 'binding-not-found' }
  }
  if (bindings.length > 1) {
    return { mode: 'error', reason: 'duplicate-binding' }
  }

  return { mode: 'subtype-bound', partition, optjobSubtype: bindings[0].optjob_subtype }
}

export function getAccessGatedDayAheadTabs(
  hasAccess: (requirements: FeatureRequirements) => boolean,
  t: TFunction
): DayAheadTabDefinition[] {
  const submoduleToTabItemMap = {
    price_scenarios: { id: DayAheadTabId.PriceScenarios, title: t('Price scenarios'), children: [] },
    // No system currently holds the dayaheadtrading_bid_management / dayaheadtrading_plan submodule
    // grants, for two unrelated reasons: bid management renders the bid config without publishing a
    // partition, which the customer's config requires and which also collides on generated bid
    // ordering once a scenario holds both partitioned and unpartitioned bids; plan is withheld as a
    // product judgement while that customer's production and delivery planners are still testing it.
    // Both are gated the same way as the other two tabs — access is simply not granted until ready.
    bid_management: { id: DayAheadTabId.BidManagement, title: t('Bid management'), children: [] },
    overview: { id: DayAheadTabId.Overview, title: t('Overview'), children: [] },
    plan: { id: DayAheadTabId.Plan, title: t('Plan'), children: [] },
  }

  return Object.entries(submoduleToTabItemMap)
    .filter(([key]) => hasAccess({ module: 'dayaheadtrading', submodule: `dayaheadtrading_${key}` }))
    .map(([, value]) => value)
}

export function getActivePartitionKey(partitions: ElplanPartition[], selectedPartitionKey: string): string {
  return partitions.some(({ key }) => key === selectedPartitionKey) ? selectedPartitionKey : (partitions[0]?.key ?? '')
}

export function hasPartitionScopedScenarios(scenarios: ElectricityPriceScenario[]): boolean {
  return scenarios.some(
    ({ partition_bindings, uiconfig_override_alias }) =>
      partition_bindings.length > 0 || ELPLAN_PARTITION_MODE_ALIAS in uiconfig_override_alias
  )
}

export function shouldShowPartitionSwitch(
  tabId: DayAheadTabId,
  visibleTabIds: DayAheadTabId[],
  hasPartitionScopedPriceScenarios: boolean
): boolean {
  if (!visibleTabIds.includes(tabId)) {
    return false
  }

  return (
    tabId === DayAheadTabId.BidManagement ||
    (tabId === DayAheadTabId.PriceScenarios && hasPartitionScopedPriceScenarios)
  )
}

export function getScenarioSubtypeAliasOptions(
  scenario: ElectricityPriceScenario,
  partitions: ElplanPartition[]
): SubtypeAliasOption[] {
  return scenario.partition_bindings.flatMap((binding) => {
    const partition = partitions.find(({ id }) => id === binding.partition_id)
    return partition ? [{ key: partition.key, value: binding.optjob_subtype, displayName: partition.display_name }] : []
  })
}

export function buildScenarioResultAliases({
  scenario,
  partitions,
  partitionResolution,
  translatedCardTitle,
  createdAt,
}: {
  scenario: ElectricityPriceScenario
  partitions: ElplanPartition[]
  partitionResolution: RenderableScenarioPartitionResolution
  translatedCardTitle: string
  createdAt: string | null
}): UiConfigAliases {
  const uiConfigOverrideAliases: UiConfigAliases = { ...scenario.uiconfig_override_alias, created_at: createdAt }
  delete uiConfigOverrideAliases[ELPLAN_PARTITION_MODE_ALIAS]
  if (partitionResolution.mode === 'partition-only') {
    delete uiConfigOverrideAliases.selected_subtype
    delete uiConfigOverrideAliases.selected_subtype_display_name
    for (const aliasKey of Object.keys(uiConfigOverrideAliases)) {
      if (aliasKey.startsWith('subtype_')) {
        delete uiConfigOverrideAliases[aliasKey]
      }
    }
  }
  if (partitionResolution.mode === 'unpartitioned') {
    return {
      ...uiConfigOverrideAliases,
      scenario_card_title: translatedCardTitle,
    }
  }

  const partitionAliases = buildPartitionAliases({
    options: partitions.map(({ key, display_name }) => ({ key, displayName: display_name })),
    selectedKey: partitionResolution.partition.key,
  })
  let subtypeAliases: UiConfigAliases = {}
  if (partitionResolution.mode === 'subtype-bound') {
    subtypeAliases = buildSubtypeAliases({
      options: getScenarioSubtypeAliasOptions(scenario, partitions),
      selectedKey: partitionResolution.partition.key,
    })
  }

  return {
    ...uiConfigOverrideAliases,
    ...partitionAliases,
    ...subtypeAliases,
    elplan_partition_id: partitionResolution.partition.id,
    scenario_card_title: `${translatedCardTitle} ${partitionResolution.partition.display_name}`,
  }
}

export function buildRegularPlanSubtypeAliases(
  partitions: ElplanPartition[],
  partitionResolution: RenderableScenarioPartitionResolution
): UiConfigAliases | undefined {
  if (partitionResolution.mode !== 'subtype-bound') {
    return undefined
  }

  return buildSubtypeAliases({
    // Regular production plans use the stable partition key as their subtype;
    // scenario optimization uses the separate subtype from the binding.
    options: partitions.map(({ key, display_name }) => ({ key, value: key, displayName: display_name })),
    selectedKey: partitionResolution.partition.key,
  })
}

export function getScenarioOptimizationDisplayName(
  translatedScenarioName: string,
  partitionResolution: RenderableScenarioPartitionResolution
): string {
  return partitionResolution.mode === 'subtype-bound'
    ? `${translatedScenarioName} ${partitionResolution.partition.display_name}`
    : translatedScenarioName
}

export function getScenarioOptimizationSubtypes(
  scenario: ElectricityPriceScenario,
  partitionResolution: RenderableScenarioPartitionResolution
): string[] | null {
  return partitionResolution.mode === 'subtype-bound' ? [partitionResolution.optjobSubtype] : scenario.optjob_subtypes
}

export function getScenarioBidEditorPartitionId(
  partitionResolution: RenderableScenarioPartitionResolution
): number | undefined {
  return partitionResolution.mode === 'unpartitioned' ? undefined : partitionResolution.partition.id
}
